import _ from 'lodash'
import type { Mesh, SceneControl } from '@anov/3d-core'
import { Group, Vector3 } from '@anov/3d-core'
import ObjectLayer from '../Base/ObjectLayer'
import type { FenceOption, FillAreaOption, Option } from './type'

const FenceStrategy = {
  base: async () => (await import('./Fence/fence_base_01')).default,
  feeting: async () => (await import('./Fence/fence_feeting_01')).default,
  roll: async () => (await import('./Fence/fence_roll_01')).default,
}
const FillAreaStrategy = {
  line: async () => (await import('./FillArea/fillArea_line_01')).default,
  face: async () => (await import('./FillArea/fillArea_face_01')).default,
}
const DEFAULT_OPTION = {
  style: 'area_base_01',
  fence: {
    uri: 'base',
    height: 70,
    alpha: 1,
    color: '#006CBF',
    visible: true,
  },
  fillArea: {
    uri: 'face',
    visible: true,
    alpha: 1,
    color: '#006CBF',
    pos: 'bottom',
  },
  surfaceSnap: false,
  posType: 0,
  coordZType: 0,
  path: {
    points: [
      {
        position: [-100, 0, 100],
      },
      {
        position: [100, 0, 100],
      },
      {
        position: [100, 0.00, -100],
      },
      {
        position: [-100, 0.00, -100],
      },
    ],
    corner: 10, // 圆角默认 10
    close: true, // 默认闭合
  },
}
const STYLE_DATA = {
  area_base_01: ['base', ''],
  area_feeting_01: ['feeting', ''],
  area_roll_01: ['roll', ''],
}
class Area extends ObjectLayer {
  private _mesh!: Group
  private _option: Option

  private _fenceMesh: Mesh & any
  private _fillAreaMesh: Mesh & any

  private _style: string
  private _surfaceSnap: boolean
  private _posType: number
  private _coordZType: number
  private _fence: FenceOption
  private _fenceProxy: FenceOption
  private _fillArea: FillAreaOption
  private _path: Option['path']
  private _pathProxy: Option['path']
  private _points!: Vector3[]
  private _corner: number
  private _close: boolean
  constructor(option: Option, sceneControl: SceneControl) {
    super(sceneControl)
    this._initParam(option)
    this._arrangePathParam(this._path)
    this._initializeMesh()
  }

  get mesh() {
    return this._mesh
  }

  get style(): string {
    return this._style
  }

  get fence(): FenceOption {
    // return this._fenceProxy
    return this._fence
  }

  get fillArea(): FillAreaOption {
    return this._fillArea
  }

  get surfaceSnap(): boolean {
    return this._surfaceSnap
  }

  set surfaceSnap(value: boolean) {
    if (this._surfaceSnap === value)
      return
    this._surfaceSnap = value
  }

  get posType(): number {
    return this._posType
  }

  set posType(value: number) {
    if (this._posType === value)
      return
    this._posType = value
  }

  get coordZType(): number {
    return this._coordZType
  }

  set coordZType(value: number) {
    if (this._coordZType === value)
      return
    this._coordZType = value
  }

  // 设置 path 时，返回代理对象
  set path(value: Option['path']) {
    if (this._path === value)
      return
    this._arrangePathParam(value)
    this._resetFence()
    this._resetFillArea()
  }

  // 获取 path 时，返回代理对象
  get path(): Option['path'] {
    return this._pathProxy // 返回代理对象
  }

  // 销毁
  dispose() {
    this.remove(this._mesh)
  }

  private _initParam(option: Option) {
    this._option = _.mergeWith(
      _.cloneDeep(DEFAULT_OPTION),
      option,
      this._arrayMergeCustomizer,
    )

    const { fence, fillArea } = this._option
    this._fence = fence
    this._fillArea = fillArea
    this._style = this._option.style
    this._path = this._option.path
    this._surfaceSnap = this._option.surfaceSnap
    this._posType = this._option.posType
    this._coordZType = this._option.coordZType
    this._createPathProxy()
    this._mesh = new Group()
    this.add(this._mesh)
    // this._createFenceProxy()
  }

  private _arrangePathParam(value: Option['path']) {
    // 确保 value 符合预期结构
    if (value && Array.isArray(value.points) && value.points.length >= 3) {
      const { points, corner, close } = value

      this._path = value // 更新 _path
      this._points = points.map(v => new Vector3(v.position[0], v.position[1], v.position[2])) // 使用 points 创建 Vector3 对象
      this._corner = corner // 更新圆角
      this._close = close // 更新闭合状态
    }

    else { console.error('Invalid path data: Ensure points has at least 3 elements') }
  }

  private async _initializeMesh() {
    const { fence, fillArea } = this._option
    const { currentFenceStyle, currentFillAreaStyle } = this._getCurrentStyle()
    if (currentFenceStyle) {
      this._fenceMesh = await this._createFence(currentFenceStyle)
      this._fenceMesh.visible = fence.visible
      this._fenceMesh.addReloadListener(this._resetFence)
      this._mesh.add(this._fenceMesh)
      this._fence = this.createProxy(this._fence, this._fenceMesh)
    }
    if (currentFillAreaStyle) {
      this._fillAreaMesh = await this._createFillArea(currentFillAreaStyle)
      this._fillAreaMesh.visible = fillArea.visible
      this._fillAreaMesh.addReloadListener(this._resetFillArea)
      this._mesh.add(this._fillAreaMesh)
      this._fillArea = this.createProxy(this._fillArea, this._fillAreaMesh)
    }
    // 统一更新矩阵
    // this._mesh.updateWorldMatrix(true, true)
  }

  // 注册样式
  private async _registerStrategy(style: string, StrategyManage: Object) {
    const strategyClass = StrategyManage[style]
    if (strategyClass)
      return await strategyClass()

    else
      throw new Error(`Style ${style} not found`)
  }

  // 创建围栏
  private async _createFence(style: string) {
    const { fence } = this._option
    const Strategy = await this._registerStrategy(style, FenceStrategy)
    const option = {
      points: this._points,
      corner: this._corner,
      close: this._close,
      ...fence,
    }
    return new Strategy(option, this)
  }

  private async _resetFence() {
    if (this._fenceMesh) {
      this._mesh.remove(this._fenceMesh)
      const { currentFenceStyle } = this._getCurrentStyle()
      this._fenceMesh = await this._createFence(currentFenceStyle)
      this._mesh.add(this._fenceMesh)
      this._fence = this.createProxy(this._fence, this._fenceMesh)
    }
  }

  // 创建封底
  private async _createFillArea(style: string) {
    const { fillArea, fence } = this._option
    const Strategy = await this._registerStrategy(style, FillAreaStrategy)
    const option = {
      points: this._points,
      corner: this._corner,
      close: this._close,
      ...fillArea,
    }
    const mesh = new Strategy(option, this)
    mesh.position.y = fillArea.pos === 'bottom' ? 0 : fence.height
    return mesh
  }

  private async _resetFillArea() {
    if (this._fillAreaMesh) {
      this._mesh.remove(this._fillAreaMesh)
      const { currentFillAreaStyle } = this._getCurrentStyle()
      this._fillAreaMesh = await this._createFillArea(currentFillAreaStyle)
      this._mesh.add(this._fillAreaMesh)
      this._fillArea = this.createProxy(this._fillArea, this._fillAreaMesh)
    }
  }

  private _createPathProxy() {
    // 创建代理，拦截对 _path 的修改
    this._pathProxy = new Proxy(this._path, {
      set: (target, prop, value) => {
        if (prop === 'corner')
          this._corner = value

        else if (prop === 'close')
          this._close = value

        else if (prop === 'points')
          this._points = value.map(v => new Vector3(v.position[0], v.position[1], v.position[2]))

        target[prop] = value // 更新值
        this._resetFence()
        this._resetFillArea()
        return true // 必须返回 true 表示操作成功
      },
    })
  }

  private _arrayMergeCustomizer(objValue, srcValue) {
    if (_.isArray(objValue) && srcValue)
      return srcValue
  }

  private _getCurrentStyle() {
    let currentFenceStyle = ''
    let currentFillAreaStyle = ''
    if (this.style === 'area_dynamic_mode') {
      currentFenceStyle = this._fence.uri
      currentFillAreaStyle = this._fillArea.uri
    }
    else {
      [currentFenceStyle, currentFillAreaStyle] = STYLE_DATA[this.style]
    }
    return { currentFenceStyle, currentFillAreaStyle }
  }
}

export { Area }